import { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Home, LayoutDashboard, FlaskConical, ShieldCheck,
  ShoppingBag, UserCog, PanelLeftClose, PanelLeftOpen,
  X, ChevronDown, LogOut, SlidersHorizontal, ExternalLink, Sparkles,
} from 'lucide-react';
import { useNav } from './NavContext';

const THEIA_URL = process.env.REACT_APP_THEIA_URL || null;

// ─── Navigation structure ────────────────────────────────────────────────────────
const SIDEBAR_SECTIONS = [
  {
    id: 'primary',
    label: null,
    items: [{ label: 'Home', icon: Home, path: '/home' }],
  },
  {
    id: 'workspace',
    label: 'Workspace',
    items: [
      {
        label: 'Workspace',
        icon: LayoutDashboard,
        path: '/workspace',
        children: [
          { label: 'Custom Build', path: '/workspace/custom-build' },
          { label: 'Service Management', path: '/workspace/services' },
          { label: 'DDLC Pipeline', path: '/workspace/ddlc' },
          { label: 'Prompt Renderer', path: '/workspace/prompt-renderer' },
        ],
      },
      {
        label: 'Data Studio',
        icon: FlaskConical,
        path: '/data-studio',
        adminOnly: true,
        children: [
          { label: 'Clustering', path: '/data-studio/segmentation' },
          { label: 'Cluster Analysis', path: '/data-studio/cluster-analysis' },
          { label: 'Metadata Extraction', path: '/data-studio/metadata' },
          { label: 'Analyze', path: '/data-studio/analyze' },
          { label: 'Query Data', path: '/data-studio/query' },
          { label: 'Knowledge Graph', path: '/data-studio/knowledge-graph' },
          { label: 'Ingest Data', path: '/data-studio/ingest' },
          { label: 'Doc Intelligence', path: '/data-studio/doc-intelligence' },
          { label: 'Data Transfer', path: '/data-studio/transfer' },
          { label: 'Investigate', path: '/data-studio/investigate' },
          { label: 'Governance Intake', path: '/data-studio/governance-intake' },
          ...(THEIA_URL ? [{ label: 'Theia IDE', href: THEIA_URL }] : []),
        ],
      },
      {
        label: 'AI Governance',
        icon: ShieldCheck,
        path: '/ai-governance',
        adminOnly: true,
        children: [
          { label: 'Responsible AI', path: '/ai-governance/responsible-ai' },
          { label: 'Agent Lineage', path: '/ai-governance/agent-lineage' },
        ],
      },
    ],
  },
  {
    id: 'discover',
    label: 'Discover',
    items: [
      { label: 'Marketplace', icon: ShoppingBag, path: '/marketplace' },
      {
        label: 'New Use Case',
        icon: Sparkles,
        path: '/workspace/prompt-renderer',
        children: [
          // accepted features are appended here automatically
        ],
      },
    ],
  },
  {
    id: 'admin',
    label: 'Admin',
    adminOnly: true,
    items: [
      {
        label: 'Administration',
        icon: UserCog,
        path: '/administration',
        children: [
          { label: 'User Management', path: '/administration/user-management' },
          { label: 'Customer & Pool Management', path: '/administration/pool-management' },
        ],
      },
    ],
  },
];

// ─── Logo section ────────────────────────────────────────────────────────────────
const LogoSection = ({ collapsed, onLogoClick, isClient = false }) => (
  <button
    onClick={onLogoClick}
    className={`
      w-full flex items-center shrink-0 border-b transition-all duration-200
      ${isClient ? 'border-gray-700/50' : 'border-gray-100'}
      ${collapsed ? 'justify-center px-2 py-4' : 'px-4 py-4'}
    `}
    aria-label="Go to home"
  >
    <div className="relative flex items-center h-8">
      <img
        src="/caplogo2.png"
        alt="Capgemini"
        className={`h-7 w-7 object-contain transition-opacity duration-200 ${collapsed ? 'opacity-100' : 'opacity-0 absolute'}`}
      />
      <img
        src="/capgemini-logo.png"
        alt="Capgemini"
        className={`h-8 object-contain transition-opacity duration-200 ${collapsed ? 'opacity-0 absolute' : 'opacity-100'}`}
      />
    </div>
  </button>
);

// ─── Single nav item ─────────────────────────────────────────────────────────────
const NavItem = ({ item, collapsed, onClick }) => {
  const location = useLocation();
  const active =
    location.pathname === item.path ||
    location.pathname.startsWith(item.path + '/');

  return (
    <div className="relative group">
      <button
        onClick={() => onClick(item.path)}
        className={`
          w-full flex items-center rounded-lg text-sm transition-colors
          ${collapsed ? 'px-0 py-2.5 justify-center' : 'px-3 py-2 gap-3'}
          ${active
            ? 'bg-primaryLight text-primary font-medium'
            : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'}
        `}
        aria-current={active ? 'page' : undefined}
        aria-label={item.label}
      >
        {active && !collapsed && (
          <span className="absolute left-0 top-1/2 -translate-y-1/2 w-0.5 h-5 bg-primary rounded-r-full" />
        )}
        <item.icon
          size={18}
          className={`shrink-0 transition-colors ${
            active ? 'text-primary' : 'text-gray-400 group-hover:text-gray-600'
          }`}
        />
        {!collapsed && <span className="truncate">{item.label}</span>}
      </button>

      {/* Tooltip when collapsed */}
      {collapsed && (
        <div
          role="tooltip"
          className="
            absolute left-full top-1/2 -translate-y-1/2 ml-3
            px-2.5 py-1.5 bg-gray-900 text-white text-xs rounded-md
            whitespace-nowrap pointer-events-none
            opacity-0 group-hover:opacity-100 transition-opacity duration-150 z-50
          "
        >
          {item.label}
          <span className="absolute right-full top-1/2 -translate-y-1/2 border-[5px] border-transparent border-r-gray-900" />
        </div>
      )}
    </div>
  );
};

// ─── Nav item with expandable children ───────────────────────────────────────────
const ExpandableNavItem = ({ item, collapsed, onNavigate, isOpen, onToggle }) => {
  const location = useLocation();
  const isParentActive =
    location.pathname.startsWith(item.path + '/') ||
    (item.children || []).some((c) => location.pathname === c.path);
  const [popupOpen, setPopupOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setPopupOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  if (collapsed) {
    return (
      <div className="relative group" ref={ref}>
        <button
          onClick={() => setPopupOpen((v) => !v)}
          className={`w-full flex items-center rounded-lg text-sm transition-colors px-0 py-2.5 justify-center ${
            isParentActive ? 'bg-primaryLight' : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
          }`}
        >
          <item.icon size={18} className={`shrink-0 ${isParentActive ? 'text-primary' : 'text-gray-400 group-hover:text-gray-600'}`} />
        </button>

        {popupOpen && (
          <div className="absolute left-full top-0 ml-2 w-48 bg-white rounded-xl shadow-xl border border-gray-100 py-1 z-50">
            <p className="px-3 py-1.5 text-[10px] font-semibold text-gray-400 uppercase tracking-widest">{item.label}</p>
            {item.children.map((child) => {
              const childActive = !child.href && location.pathname === child.path;
              return (
                <div key={child.href || child.path} className="relative">
                  {child.href ? (
                    <a
                      href={child.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={() => setPopupOpen(false)}
                      className="w-full flex items-center justify-between px-3 py-2 text-sm transition-colors text-gray-700 hover:bg-gray-50 hover:text-gray-900"
                    >
                      {child.label}
                      <ExternalLink size={12} className="shrink-0 text-gray-400" />
                    </a>
                  ) : (
                    <>
                      {childActive && (
                        <span className="absolute left-0 top-1/2 -translate-y-1/2 w-0.5 h-4 bg-primary rounded-r-full" />
                      )}
                      <button
                        onClick={() => { onNavigate(child.path); setPopupOpen(false); }}
                        className={`w-full text-left px-3 py-2 text-sm transition-colors ${
                          childActive ? 'text-primary font-medium' : 'text-gray-700 hover:bg-gray-50 hover:text-gray-900'
                        }`}
                      >
                        {child.label}
                      </button>
                    </>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {!popupOpen && (
          <div
            role="tooltip"
            className="absolute left-full top-1/2 -translate-y-1/2 ml-3 px-2.5 py-1.5 bg-gray-900 text-white text-xs rounded-md whitespace-nowrap pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity duration-150 z-50"
          >
            {item.label}
            <span className="absolute right-full top-1/2 -translate-y-1/2 border-[5px] border-transparent border-r-gray-900" />
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="group">
      {/* Parent row — never highlighted, only the active child gets the indicator */}
      <button
        onClick={() => onToggle(item.path)}
        className="w-full flex items-center rounded-lg text-sm transition-colors px-3 py-2 gap-3 text-gray-600 hover:bg-gray-100 hover:text-gray-900"
      >
        <item.icon size={18} className={`shrink-0 ${isParentActive ? 'text-primary' : 'text-gray-400 group-hover:text-gray-600'}`} />
        <span className="flex-1 truncate text-left">{item.label}</span>
        <ChevronDown size={14} className={`shrink-0 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      <div className={`transition-all duration-200 ${isOpen ? 'max-h-[55vh] overflow-y-auto' : 'max-h-0 overflow-hidden'}`}>
        <div className="ml-5 mt-0.5 space-y-0.5 border-l border-gray-100 pl-2 pb-1">
          {item.children.map((child) => {
            const childActive = !child.href && location.pathname === child.path;
            return (
              <div key={child.href || child.path} className="relative">
                {child.href ? (
                  <a
                    href={child.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-full flex items-center justify-between px-2 py-1.5 rounded-md text-xs transition-colors text-gray-500 hover:text-gray-800 hover:bg-gray-50"
                  >
                    {child.label}
                    <ExternalLink size={11} className="shrink-0 text-gray-400" />
                  </a>
                ) : (
                  <>
                    {childActive && (
                      <span className="absolute left-0 top-1/2 -translate-y-1/2 w-0.5 h-4 bg-primary rounded-r-full" />
                    )}
                    <button
                      onClick={() => onNavigate(child.path)}
                      className={`w-full text-left px-2 py-1.5 rounded-md text-xs transition-colors ${
                        childActive ? 'text-primary font-medium bg-primaryLight' : 'text-gray-500 hover:text-gray-800 hover:bg-gray-50'
                      }`}
                    >
                      {child.label}
                    </button>
                  </>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

// ─── Section divider / label ─────────────────────────────────────────────────────
const SectionLabel = ({ label, collapsed }) =>
  collapsed ? (
    <div className="mx-3 my-2 border-t border-gray-100" />
  ) : (
    <p className="px-3 pt-1 pb-1.5 text-[10px] font-semibold text-gray-400 uppercase tracking-widest select-none">
      {label}
    </p>
  );

// ─── User section (bottom of sidebar) ───────────────────────────────────────────
const UserSection = ({ collapsed, isClient = false }) => {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const ref = useRef(null);

  const username =
    sessionStorage.getItem('username') ||
    sessionStorage.getItem('user_name') ||
    'User';
  const role = sessionStorage.getItem('role') || 'user';

  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleLogout = () => {
    ['token', 'user_id', 'username', 'user_name', 'role', 'thread_id'].forEach((k) =>
      sessionStorage.removeItem(k)
    );
    navigate('/a3-login');
  };

  const menuItems = (
    <>
      {!isClient && (
        <button
          onClick={() => { navigate('/app-settings'); setOpen(false); }}
          className={`w-full flex items-center gap-3 px-3 py-2 text-sm rounded-lg transition-colors text-left
            ${isClient ? 'text-gray-300 hover:bg-gray-800 hover:text-primary' : 'text-gray-700 hover:bg-gray-50 hover:text-primary'}`}
        >
          <SlidersHorizontal size={15} className="text-gray-400 shrink-0" />
          App Settings
        </button>
      )}
      <button
        onClick={() => { setOpen(false); setShowLogoutConfirm(true); }}
        className="w-full flex items-center gap-3 px-3 py-2 text-sm text-error hover:bg-errorLight rounded-lg transition-colors text-left"
      >
        <LogOut size={15} className="shrink-0" />
        Log out
      </button>
    </>
  );

  return (
    <>
      <div className={`shrink-0 border-t ${isClient ? 'border-gray-700/50' : 'border-gray-100'}`} ref={ref}>

        {/* ── Expanded sidebar: inline accordion ── */}
        {!collapsed && (
          <div className="p-2">
            <button
              onClick={() => setOpen((v) => !v)}
              className={`w-full flex items-center gap-3 rounded-lg px-2 py-2 transition-colors
                ${isClient ? 'hover:bg-gray-800' : 'hover:bg-gray-100'}`}
              aria-haspopup="menu"
              aria-expanded={open}
              aria-label="User menu"
            >
              <div className="w-7 h-7 rounded-full bg-secondary flex items-center justify-center text-white text-xs font-semibold shrink-0">
                {username.charAt(0).toUpperCase()}
              </div>
              <div className="flex-1 text-left min-w-0">
                <p className={`text-sm font-medium truncate ${isClient ? 'text-gray-100' : 'text-gray-800'}`}>{username}</p>
                <p className="text-xs text-gray-400 capitalize truncate">{role}</p>
              </div>
              <ChevronDown
                size={14}
                className={`text-gray-400 shrink-0 transition-transform duration-200 ${open ? 'rotate-180' : ''}`}
              />
            </button>
            <div className={`overflow-hidden transition-all duration-200 ${open ? 'max-h-24 mt-1' : 'max-h-0'}`}>
              <div className="space-y-0.5">{menuItems}</div>
            </div>
          </div>
        )}

        {/* ── Collapsed sidebar: icon + right popup ── */}
        {collapsed && (
          <div className="relative group p-2">
            <button
              onClick={() => setOpen((v) => !v)}
              className={`w-full flex justify-center rounded-lg py-2 transition-colors
                ${isClient ? 'hover:bg-gray-800' : 'hover:bg-gray-100'}`}
              aria-haspopup="menu"
              aria-expanded={open}
              aria-label="User menu"
            >
              <div className="w-7 h-7 rounded-full bg-secondary flex items-center justify-center text-white text-xs font-semibold">
                {username.charAt(0).toUpperCase()}
              </div>
            </button>
            {open && (
              <div className={`absolute left-full bottom-0 ml-2 w-52 rounded-xl shadow-xl border py-2 z-50
                ${isClient ? 'bg-gray-900 border-gray-700/50' : 'bg-white border-gray-100'}`}>
                <div className={`px-3 pb-2 mb-1 border-b ${isClient ? 'border-gray-700/50' : 'border-gray-100'}`}>
                  <p className={`text-sm font-semibold truncate ${isClient ? 'text-gray-100' : 'text-gray-900'}`}>{username}</p>
                  <p className="text-xs text-gray-400 capitalize">{role}</p>
                </div>
                <div className="px-1">{menuItems}</div>
              </div>
            )}
            {!open && (
              <div
                role="tooltip"
                className="absolute left-full top-1/2 -translate-y-1/2 ml-3 px-2.5 py-1.5 bg-gray-900 text-white text-xs rounded-md whitespace-nowrap pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity duration-150 z-50"
              >
                {username}
                <span className="absolute right-full top-1/2 -translate-y-1/2 border-[5px] border-transparent border-r-gray-900" />
              </div>
            )}
          </div>
        )}
      </div>

      {/* Logout confirmation modal */}
      {showLogoutConfirm && (
        <div
          className="fixed inset-0 bg-black/50 flex justify-center items-center z-[1000] backdrop-blur-sm"
          role="dialog"
          aria-modal="true"
          aria-labelledby="logout-title"
        >
          <div className="bg-white rounded-xl p-6 shadow-xl max-w-sm w-full mx-4">
            <h3 id="logout-title" className="text-base font-semibold mb-1">
              Confirm Logout
            </h3>
            <p className="text-sm text-gray-500 mb-5">
              Are you sure you want to log out?
            </p>
            <div className="flex justify-end gap-2">
              <button
                className="px-4 py-2 text-sm border border-gray-200 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
                onClick={() => setShowLogoutConfirm(false)}
              >
                Cancel
              </button>
              <button
                className="px-4 py-2 text-sm bg-error text-white rounded-lg hover:opacity-90 transition-opacity"
                onClick={handleLogout}
              >
                Log out
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

// ─── Sidebar content (shared between desktop and mobile) ────────────────────────
const SidebarContent = ({ collapsed, onNavigate, onToggleCollapse, showLogo, isClient = false }) => {
  const isAdmin = sessionStorage.getItem('role') === 'admin';
  const location = useLocation();

  const findOpenPath = (pathname) => {
    for (const section of SIDEBAR_SECTIONS) {
      for (const item of section.items) {
        if (item.children && pathname.startsWith(item.path + '/')) return item.path;
      }
    }
    return null;
  };

  const [openPath, setOpenPath] = useState(() => findOpenPath(location.pathname));

  useEffect(() => {
    const matched = findOpenPath(location.pathname);
    if (matched) setOpenPath(matched);
  }, [location.pathname]);

  const handleToggle = (path) => setOpenPath((prev) => (prev === path ? null : path));

  return (
    <div className="flex flex-col h-full relative">
      {/* Logo */}
      {showLogo && (
        <LogoSection collapsed={collapsed} onLogoClick={() => onNavigate('/home')} isClient={isClient} />
      )}

      {/* Navigation sections — hidden for client role */}
      {!isClient && (
        <nav
          className="flex-1 overflow-y-auto overflow-x-hidden py-3 px-2 space-y-0.5 scrollbar-thin"
          aria-label="Primary navigation"
        >
          {SIDEBAR_SECTIONS.map((section) => {
            if (section.adminOnly && !isAdmin) return null;
            const visibleItems = section.items.filter((item) => !item.adminOnly || isAdmin);
            if (visibleItems.length === 0) return null;

            return (
              <div key={section.id} className={section.label ? 'pt-1' : ''}>
                {section.label && <SectionLabel label={section.label} collapsed={collapsed} />}
                <div className="space-y-0.5">
                  {visibleItems.map((item) =>
                    item.children ? (
                      <ExpandableNavItem
                        key={item.path}
                        item={item}
                        collapsed={collapsed}
                        onNavigate={onNavigate}
                        isOpen={openPath === item.path}
                        onToggle={handleToggle}
                      />
                    ) : (
                      <NavItem key={item.path} item={item} collapsed={collapsed} onClick={onNavigate} />
                    )
                  )}
                </div>
              </div>
            );
          })}
        </nav>
      )}

      {/* Spacer for client role so UserSection stays at the bottom */}
      {isClient && <div className="flex-1" />}

      {/* User section */}
      <UserSection collapsed={collapsed} isClient={isClient} />

      {/* Collapse toggle — only on desktop, not for client */}
      {!isClient && onToggleCollapse && (
        <div className="shrink-0 px-2 pb-2">
          <button
            onClick={onToggleCollapse}
            className={`
              w-full flex items-center rounded-lg px-2 py-2 text-xs
              text-gray-400 hover:bg-gray-100 hover:text-gray-600 transition-colors
              ${collapsed ? 'justify-center' : 'gap-2'}
            `}
            aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            {collapsed ? (
              <PanelLeftOpen size={15} />
            ) : (
              <>
                <PanelLeftClose size={15} />
                <span>Collapse</span>
              </>
            )}
          </button>
        </div>
      )}
    </div>
  );
};

// ─── Main export ─────────────────────────────────────────────────────────────────
const AppSidebar = () => {
  const navigate = useNavigate();
  const {
    sidebarCollapsed, toggleSidebar,
    mobileSidebarOpen, closeMobileSidebar,
    hoverExpandEnabled,
    sidebarHoverExpanded: isHoverExpanded,
    setSidebarHoverExpanded: setIsHoverExpanded,
  } = useNav();

  const isClient = sessionStorage.getItem('role')?.toLowerCase() === 'client';

  const navTo = (path) => {
    navigate(path);
    closeMobileSidebar();
  };

  return (
    <>
      {/* ── Desktop sidebar (full height, no top bar) ──────────────────────── */}
      <aside
        className={`
          hidden md:flex flex-col
          fixed left-0 top-0 z-40 h-screen border-r
          transition-all duration-500 ease-in-out
          ${isClient ? 'bg-gray-900 border-gray-700/50' : 'bg-white border-gray-100'}
          ${sidebarCollapsed && !isHoverExpanded ? 'w-16' : 'w-60'}
        `}
        aria-label="Sidebar navigation"
        onMouseEnter={() => !isClient && hoverExpandEnabled && sidebarCollapsed && setIsHoverExpanded(true)}
        onMouseLeave={() => setIsHoverExpanded(false)}
      >
        <SidebarContent
          collapsed={sidebarCollapsed && !isHoverExpanded}
          onNavigate={navTo}
          onToggleCollapse={toggleSidebar}
          showLogo
          isClient={isClient}
        />
      </aside>

      {/* ── Mobile overlay drawer ──────────────────────────────────────────── */}
      {mobileSidebarOpen && (
        <>
          <div
            className="md:hidden fixed inset-0 bg-black/40 z-40 backdrop-blur-sm"
            onClick={closeMobileSidebar}
            aria-hidden="true"
          />
          <aside
            className={`md:hidden fixed left-0 top-0 h-full w-64 shadow-2xl z-50 flex flex-col
              ${isClient ? 'bg-gray-900' : 'bg-white'}`}
            role="dialog"
            aria-modal="true"
            aria-label="Mobile sidebar navigation"
          >
            {/* Mobile drawer header */}
            <div className={`flex items-center justify-between px-4 h-14 border-b shrink-0
              ${isClient ? 'border-gray-700/50' : 'border-gray-100'}`}>
              <img src="/capgemini-logo.png" alt="Capgemini" className="h-7 object-contain" />
              <button
                onClick={closeMobileSidebar}
                className={`p-1.5 rounded-md transition-colors
                  ${isClient ? 'text-gray-400 hover:bg-gray-800' : 'text-gray-400 hover:bg-gray-100'}`}
                aria-label="Close navigation"
              >
                <X size={18} />
              </button>
            </div>
            {/* Nav (no logo duplicate, no collapse toggle on mobile) */}
            <div className="flex-1 overflow-hidden">
              <SidebarContent
                collapsed={false}
                onNavigate={navTo}
                onToggleCollapse={null}
                showLogo={false}
                isClient={isClient}
              />
            </div>
          </aside>
        </>
      )}
    </>
  );
};

export default AppSidebar;
